import { useState, useEffect } from 'react';
import { useLocalStorage } from './useLocalStorage';

export const useStreaks = () => {
  const [streakData, setStreakData] = useLocalStorage('streakData', {
    currentStreak: 0,
    longestStreak: 0,
    lastStudyDate: null,
    studyDates: [] // Array of date strings
  });

  const [todayStudied, setTodayStudied] = useState(false);

  // Get today's date as YYYY-MM-DD string
  const getTodayString = () => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  };

  // Get yesterday's date as YYYY-MM-DD string
  const getYesterdayString = () => {
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    return yesterday.toISOString().split('T')[0];
  };

  // Check if user studied today
  useEffect(() => {
    const today = getTodayString();
    setTodayStudied(streakData.studyDates.includes(today));
  }, [streakData.studyDates]);

  // Update streak when user studies
  const recordStudySession = () => {
    const today = getTodayString();

    // Don't record if already studied today
    if (streakData.studyDates.includes(today)) {
      return;
    }

    const yesterday = getYesterdayString();
    const lastStudyDate = streakData.lastStudyDate;

    let newCurrentStreak = 1;

    // If studied yesterday, increment streak
    if (lastStudyDate === yesterday) {
      newCurrentStreak = streakData.currentStreak + 1;
    }
    // If studied today for first time (or missed days), reset to 1
    else if (!lastStudyDate || lastStudyDate !== today) {
      newCurrentStreak = 1;
    }

    const newLongestStreak = Math.max(newCurrentStreak, streakData.longestStreak);
    const newStudyDates = [...new Set([...streakData.studyDates, today])]; // Add today, ensure unique

    setStreakData({
      currentStreak: newCurrentStreak,
      longestStreak: newLongestStreak,
      lastStudyDate: today,
      studyDates: newStudyDates
    });

    setTodayStudied(true);
  };

  // Check and update streak on app load
  useEffect(() => {
    const today = getTodayString();
    const yesterday = getYesterdayString();
    const lastStudyDate = streakData.lastStudyDate;

    // If last study was before yesterday, streak is broken
    if (lastStudyDate && lastStudyDate !== today && lastStudyDate !== yesterday) {
      // Check if it's actually broken (more than 1 day gap)
      const lastDate = new Date(lastStudyDate);
      const todayDate = new Date(today);
      const daysDifference = Math.floor((todayDate - lastDate) / (1000 * 60 * 60 * 24));

      if (daysDifference > 1) {
        // Streak is broken, but don't reset longestStreak
        setStreakData(prev => ({
          ...prev,
          currentStreak: 0
        }));
      }
    }
  }, []);

  return {
    currentStreak: streakData.currentStreak,
    longestStreak: streakData.longestStreak,
    todayStudied,
    studyDates: streakData.studyDates,
    recordStudySession,
    getTodayString
  };
};
